/**
 * Route Configuration System
 *
 * Centralized route definitions with lazy loading support.
 * Enables code splitting and simplified App.tsx refactoring.
 */

import { lazy } from 'react'
import type React from 'react'

/**
 * Configuration for a single route
 */
export interface RouteConfig {
  /** Path for React Router */
  path: string

  /** Lazy-loaded component */
  component: () => Promise<{ default: React.ComponentType<any> }>

  /** Whether route requires authentication */
  auth?: boolean

  /** PageShell configuration type */
  shell?: 'bgs' | 'escape-route' | 'none'

  /** Page title for browser/shell */
  title?: string

  /** Page description for shell */
  description?: string

  /** Related routes to prefetch on hover/focus */
  prefetch?: string[]

  /** Aria label for accessibility */
  ariaLabel?: string
}

/**
 * Complete routes configuration for BGS Core
 *
 * All 84+ routes organized by feature/category for optimal code splitting.
 * Each route supports:
 * - Lazy loading with automatic code splitting
 * - Authentication protection
 * - PageShell configuration
 * - Route prefetching hints
 */
export const routes: RouteConfig[] = [
  // ===========================
  // AUTH & HOME (No shell)
  // ===========================
  {
    path: '/',
    component: () => import('../pages/HomePage'),
    auth: true,
    shell: 'bgs',
    title: 'Home',
    description: 'Inventory dashboard and overview',
    prefetch: ['/search', '/intake'],
  },
  {
    path: '/login',
    component: () => import('../pages/LoginPage'),
    auth: false,
    shell: 'none',
    title: 'Login',
  },
  {
    path: '/signin',
    component: () => import('../pages/SignIn'),
    auth: false,
    shell: 'none',
    title: 'Sign In',
  },
  {
    path: '/ebay/callback',
    component: () => import('../pages/EbayCallbackPage'),
    auth: false,
    shell: 'none',
  },

  // ===========================
  // DESIGN SYSTEM DEMOS
  // ===========================
  // Temporarily disabled - V2 components not yet implemented
  // {
  //   path: '/glass-demo',
  //   component: () => import('../pages/GlassDemoPage'),
  //   auth: false,
  //   shell: 'none',
  //   title: 'Glass Demo',
  //   description: 'Orange Nebulous Glass design system demo',
  // },
  // {
  //   path: '/item-detail-mockup',
  //   component: () => import('../pages/ItemDetailMockupPage'),
  //   auth: false,
  //   shell: 'none',
  //   title: 'Item Detail Mockup',
  //   description: 'Orange Nebulous Glass item detail mockup',
  // },
  {
    path: '/retro-demo',
    component: () => import('../pages/RetroFuturisticDemoPage'),
    auth: false,
    shell: 'none',
    title: 'Retro-Futuristic Demo',
    description: 'CRT Terminal and LCD Gadget components showcase',
  },
  {
    path: '/admin-crt',
    component: () => import('../pages/AdminPageCRT'),
    auth: true,
    shell: 'none',
    title: 'Admin Terminal',
    description: 'CRT-style admin interface',
  },

  // ===========================
  // CORE INVENTORY OPERATIONS
  // ===========================
  {
    path: '/search',
    component: () => import('../pages/SearchPage'),
    auth: true,
    shell: 'bgs',
    title: 'Search Items',
    description: 'Find and manage inventory items',
    prefetch: ['/items/:id', '/review'],
  },
  {
    path: '/fubar',
    component: () => import('../pages/FubarPage'),
    auth: true,
    shell: 'bgs',
    title: 'FUBAR Items',
    description: 'Items that need attention - Fix A Fuck Up',
  },
  {
    path: '/review',
    component: () => import('../pages/ReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Review Items',
    description: 'Items pending review and processing',
  },
  {
    path: '/pallet-processing',
    component: () => import('../pages/PalletProcessingPage'),
    auth: true,
    shell: 'bgs',
    title: 'Pallet Processing',
    description: 'Items staged on the pallet awaiting assessment',
  },
  {
    path: '/intake',
    component: () => import('../pages/IntakePage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Inventory Intake',
    description: 'Add new items to your inventory',
  },
  {
    path: '/intake/:itemId',
    component: () => import('../pages/IntakePage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Inventory Intake',
    description: 'Add new items to your inventory',
  },
  {
    path: '/intake/quick/:itemId',
    component: () => import('../pages/IntakePage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Inventory Intake',
  },
  {
    path: '/items/:id',
    component: () => import('../pages/ItemDetailPage'),
    auth: true,
    shell: 'bgs',
    title: 'Item Details',
    description: 'View and edit item information',
  },

  // ===========================
  // QUICK INTAKE VARIANTS
  // ===========================
  {
    path: '/quick-intake',
    component: () => import('../pages/QuickIntakePage'),
    auth: true,
    shell: 'bgs',
    title: 'Quick Intake',
    description: 'Streamlined inventory entry',
  },

  // ===========================
  // SPEED INTAKE & REVIEW
  // ===========================
  {
    path: '/speed-intake',
    component: () => import('../pages/SpeedIntakePage'),
    auth: true,
    shell: 'bgs',
    title: 'Speed Intake',
    description: 'AI-powered rapid inventory entry',
  },
  {
    path: '/speed-review',
    component: () => import('../pages/SpeedReviewIntakePage'),
    auth: true,
    shell: 'bgs',
    title: 'Speed Review',
    description: 'Gemini 2.0 Flash AI intake with targeted aspect extraction',
  },
  {
    path: '/speed-review/queue',
    component: () => import('../pages/SpeedReviewQueuePage'),
    auth: true,
    shell: 'bgs',
    title: 'Speed Review Queue',
    description: 'Review and approve AI-analyzed items',
  },

  // ===========================
  // IN-FIELD ASSESSMENT
  // ===========================
  {
    path: '/in-field-assessment',
    component: () => import('../pages/InFieldAssessmentPage'),
    auth: true,
    shell: 'bgs',
    title: 'In-Field Assessment',
    description: 'AI-powered item identification and ROI analysis',
  },
  {
    path: '/in-field-assessment/history',
    component: () => import('../pages/IFAHistoryPage'),
    auth: true,
    shell: 'bgs',
    title: 'IFA History',
    description: 'Review past field assessments and reconsider decisions',
  },

  // ===========================
  // IMAGE-ONLY INTAKE
  // ===========================
  {
    path: '/image-intake',
    component: () => import('../pages/ImageOnlyIntakePage'),
    auth: true,
    shell: 'bgs',
    title: 'Image-Only Intake',
    description: 'Upload images for background AI processing',
  },
  {
    path: '/processing-queue',
    component: () => import('../pages/ProcessingQueuePage'),
    auth: true,
    shell: 'bgs',
    title: 'Processing Queue',
    description: 'Monitor AI image processing pipeline',
  },

  // ===========================
  // SPECIALIZED REVIEW: WATCHES
  // ===========================
  {
    path: '/watch-review',
    component: () => import('../pages/WatchReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Watch Review',
    description: 'Review watch intake items',
  },

  // ===========================
  // SPECIALIZED REVIEW: CLOTHING
  // ===========================
  {
    path: '/clothing-review',
    component: () => import('../pages/ClothingReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Clothing Review',
    description: 'Review clothing intake items',
  },

  // ===========================
  // GENERIC REVIEW VARIANTS
  // ===========================
  {
    path: '/manga-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Manga Review',
    description: 'Review manga intake items',
  },
  {
    path: '/electronics-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Electronics Review',
    description: 'Review electronics intake items',
  },
  {
    path: '/footwear-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Footwear Review',
    description: 'Review footwear intake items',
  },
  {
    path: '/toys-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Toys Review',
    description: 'Review toys intake items',
  },
  {
    path: '/movies-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Movies Review',
    description: 'Review movies intake items',
  },
  {
    path: '/collectibles-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Collectibles Review',
    description: 'Review collectibles intake items',
  },
  {
    path: '/home-garden-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Home & Garden Review',
    description: 'Review home & garden intake items',
  },
  {
    path: '/health-beauty-review',
    component: () => import('../pages/GenericReviewPage'),
    auth: true,
    shell: 'bgs',
    title: 'Health & Beauty Review',
    description: 'Review health & beauty intake items',
  },

  // ===========================
  // SCANNER & MOVEMENT
  // ===========================
  {
    path: '/qr-scanner',
    component: () => import('../pages/QRScannerPage'),
    auth: false,
    shell: 'bgs',
    title: 'QR/Barcode Scanner',
    description: 'Scan QR codes and barcodes',
  },
  {
    path: '/scanner',
    component: () => import('../pages/ScannerPage'),
    auth: true,
    shell: 'bgs',
    title: 'Scanner',
    description: 'Unified barcode, QR code, and UPC scanner',
  },
  {
    path: '/scanner/quick-intake',
    component: () => import('../pages/QuickIntakePage'),
    auth: true,
    shell: 'bgs',
    title: 'Quick Intake',
    description: 'Streamlined inventory entry',
  },
  {
    path: '/scanner/item-move',
    component: () => import('../pages/RapidMovePage'),
    auth: false,
    shell: 'bgs',
    title: 'Rapid Move',
    description: 'Quick item movement using barcode scanning',
  },
  {
    path: '/rapid-move',
    component: () => import('../pages/RapidMovePage'),
    auth: false,
    shell: 'bgs',
    title: 'Rapid Move',
    description: 'Quick item movement using barcode scanning',
  },

  // ===========================
  // ANALYTICS & REPORTS
  // ===========================
  {
    path: '/analytics',
    component: () => import('../pages/AnalyticsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Analytics Dashboard',
    description: 'View reports and analytics',
  },
  {
    path: '/qr-generator',
    component: () => import('../pages/QRGeneratorPage'),
    auth: false,
    shell: 'bgs',
    title: 'QR Code Generator',
    description: 'Generate QR codes',
  },
  {
    path: '/mobile',
    component: () => import('../pages/MobilePage'),
    auth: false,
    shell: 'bgs',
    title: 'Mobile Dashboard',
    description: 'Mobile-optimized interface',
  },
  {
    path: '/enter-sale',
    component: () => import('../pages/EnterSalePage'),
    auth: false,
    shell: 'bgs',
    title: 'Enter Sale Details',
    description: 'Record sale information',
  },
  {
    path: '/flea-market',
    component: () => import('../pages/FleaMarketPage'),
    auth: false,
    shell: 'bgs',
    title: 'Flea Market',
    description: 'Items staged for flea market sale',
  },
  {
    path: '/picklists',
    component: () => import('../pages/PickListsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Pick Lists',
    description: 'Manage and process pick lists',
  },
  {
    path: '/macbid-picklist',
    component: () => import('../pages/MacbidPickListPage'),
    auth: false,
    shell: 'bgs',
    title: 'MacBid Pick List',
    description: 'Greenville mac.bid pickup checklist',
  },
  {
    path: '/bulk-operations',
    component: () => import('../pages/BulkOperationsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Bulk Operations',
    description: 'Perform actions on multiple items',
  },
  {
    path: '/export',
    component: () => import('../pages/ExportPage'),
    auth: false,
    shell: 'bgs',
    title: 'Export Inventory',
    description: 'Export inventory data in multiple formats',
  },

  // ===========================
  // PRICING
  // ===========================
  {
    path: '/pricing',
    component: () => import('../pages/PricingPage'),
    auth: true,
    shell: 'bgs',
    title: 'AI Pricing Intelligence',
    description: 'Multi-AI seasonal pricing with knowledge base optimization',
  },
  {
    path: '/pricing/:itemId',
    component: () => import('../pages/PricingPage'),
    auth: true,
    shell: 'bgs',
    title: 'AI Pricing Intelligence',
    description: 'Get pricing recommendations for this item',
  },

  // ===========================
  // EBAY EXPORT & MARKETPLACE
  // ===========================
  {
    path: '/ebay/drafts',
    component: () => import('../pages/EbayDraftsPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Drafts',
  },
  {
    path: '/send-to-ebay',
    component: () => import('../pages/ExportQueuePage'),
    auth: true,
    shell: 'bgs',
    title: 'Send to eBay',
    description: 'Export items to eBay marketplace',
  },
  {
    path: '/send-to-ebay/:id',
    component: () => import('../pages/MarketExportPage'),
    auth: true,
    shell: 'bgs',
    title: 'Market Export',
    description: 'Prepare item for eBay listing',
  },
  {
    path: '/ebay-agent/:id',
    component: () => import('../pages/EbaySubmissionAgentPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Submission Agent',
  },

  // ===========================
  // LABEL DESIGNER
  // ===========================
  {
    path: '/quick-label-designer',
    component: () => import('../pages/QuickLabelDesignerPage'),
    auth: false,
    shell: 'none',
    title: 'Quick Label Designer',
  },

  // ===========================
  // PACKAGING
  // ===========================
  {
    path: '/packaging-management',
    component: () => import('../pages/PackagingManagementPage'),
    auth: true,
    shell: 'bgs',
    title: 'Packaging Management',
    description: 'Manage packaging types and inventory',
  },

  // ===========================
  // ESCAPEROUTE FINANCE
  // ===========================
  {
    path: '/escaperoute/*',
    component: () => import('../pages/escaperoute/EscapeRoutePage'),
    auth: true,
    shell: 'none', // EscapeRoute pages handle their own PageShell internally
    title: 'EscapeRoute',
    description: 'Personal finance and budgeting',
  },

  // ===========================
  // ADMIN MAIN & MARKETPLACE
  // ===========================
  {
    path: '/admin',
    component: () => import('../pages/AdminPage'),
    auth: true,
    shell: 'bgs',
    title: 'Administration',
    description: 'System administration and settings',
  },
  {
    path: '/admin/marketplace',
    component: () => import('../pages/admin/MarketplaceHubPage'),
    auth: true,
    shell: 'bgs',
    title: 'Marketplace Hub',
    description: 'Multi-platform marketplace integration center',
  },
  {
    path: '/admin/nifty-inventory',
    component: () => import('../pages/admin/NiftyInventoryPage.trpc'),
    auth: true,
    shell: 'none',
  },
  {
    path: '/admin/etsy-candidates',
    component: () => import('../pages/admin/EtsyCandidatesPage.trpc'),
    auth: true,
    shell: 'none',
  },
  {
    path: '/admin/social-promotion',
    component: () => import('../pages/admin/SocialPromotionDashboardPage'),
    auth: true,
    shell: 'bgs',
    title: 'Social Promotion',
    description: 'AI-powered social media content generation and publishing',
  },

  // ===========================
  // ADMIN: INVENTORY MANAGEMENT
  // ===========================
  {
    path: '/admin/categories',
    component: () => import('../pages/admin/CategoriesPage'),
    auth: false,
    shell: 'bgs',
    title: 'Categories Management',
    description: 'Manage product categories',
  },
  {
    path: '/admin/custom-fields',
    component: () => import('../pages/admin/CustomFieldsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Custom Fields Management',
    description: 'Configure custom fields',
  },
  {
    path: '/admin/locations',
    component: () => import('../pages/admin/LocationsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Locations Management',
    description: 'Manage storage locations',
  },
  {
    path: '/admin/totes',
    component: () => import('../pages/admin/TotesPage-Enhanced'),
    auth: false,
    shell: 'none',
  },
  {
    path: '/admin/shelves',
    component: () => import('../pages/admin/ShelvesPage'),
    auth: false,
    shell: 'none',
  },
  {
    path: '/admin/shelves/:id/inventory',
    component: () => import('../pages/admin/ShelfInventoryPage'),
    auth: false,
    shell: 'none',
  },

  // ===========================
  // ADMIN: AI & PROMPTS
  // ===========================
  {
    path: '/admin/ai-prompts',
    component: () => import('../pages/admin/AIPromptsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Universal AI Prompt',
    description: 'Manage AI prompts',
  },
  {
    path: '/admin/ai-monitoring',
    component: () => import('../pages/admin/AIMonitoringPage'),
    auth: false,
    shell: 'bgs',
    title: 'AI Monitoring Dashboard',
    description: 'Monitor AI processing jobs',
  },
  {
    path: '/admin/ai-provider',
    component: () => import('../pages/admin/AIProviderSettingsPage'),
    auth: true,
    shell: 'bgs',
    title: 'AI Provider Settings',
    description: 'Configure primary and fallback AI providers',
  },
  {
    path: '/admin/ai-models',
    component: () => import('../pages/admin/AIModelsPage'),
    auth: true,
    shell: 'none',
  },

  // ===========================
  // ADMIN: LABELS & PRINTING
  // ===========================
  {
    path: '/admin/label-designer',
    component: () => import('../pages/admin/LabelDesignerPage'),
    auth: false,
    shell: 'none',
  },
  {
    path: '/admin/label-printing',
    component: () => import('../pages/admin/LabelPrintingPage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Label Printing',
    description: 'Generate and print thermal labels',
  },
  {
    path: '/admin/bulk-label-printing',
    component: () => import('../pages/admin/BulkPrintLabelsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Bulk Label Printing',
    description: 'Import ToteScan data and manage print operations',
  },
  {
    path: '/admin/print-ad-hoc-label',
    component: () => import('../pages/admin/PrintAdHocLabelPage'),
    auth: false,
    shell: 'bgs',
    title: 'Print Ad Hoc Labels',
    description: 'Quick label printing - search by IC, SKU, or title',
  },

  // ===========================
  // ADMIN: MOVEMENT & OPERATIONS
  // ===========================
  {
    path: '/admin/bulk-move',
    component: () => import('../pages/admin/BulkMovePage'),
    auth: false,
    shell: 'bgs',
    title: 'Bulk Move Operations',
    description: 'Move items between totes, shelves, and locations with rollback support',
  },
  {
    path: '/admin/mass-move',
    component: () => import('../pages/admin/MassMovePage'),
    auth: false,
    shell: 'bgs',
    title: 'Mass Move',
    description: 'Bulk item movement - search by IC, SKU, or title',
  },
  {
    path: '/admin/totescan-mapping',
    component: () => import('../pages/ToteScanMappingPage'),
    auth: false,
    shell: 'bgs',
    title: 'ToteScan Label Migration',
    description: 'Map ToteScan totes to BGS locations and print labels',
  },

  // ===========================
  // ADMIN: USERS & NOTIFICATIONS
  // ===========================
  {
    path: '/admin/users',
    component: () => import('../pages/admin/UsersPage'),
    auth: false,
    shell: 'bgs',
    title: 'Users Management',
    description: 'Manage user accounts',
  },
  {
    path: '/admin/notifications',
    component: () => import('../pages/admin/NotificationRecipientsPage'),
    auth: false,
    shell: 'bgs',
    title: 'Notification Recipients',
    description: 'Manage sale alert recipients',
  },
  {
    path: '/admin/gmail-monitor',
    component: () => import('../pages/admin/GmailSalesMonitorPage'),
    auth: true,
    shell: 'bgs',
    title: 'Gmail Sales Monitor',
    description: 'Monitor Gmail for marketplace sale emails',
  },

  // ===========================
  // ADMIN: SYSTEM & MONITORING
  // ===========================
  {
    path: '/admin/system-health',
    component: () => import('../pages/admin/SystemHealthPage'),
    auth: false,
    shell: 'none',
  },
  {
    path: '/admin/system-picks',
    component: () => import('../pages/admin/SystemPicksPage'),
    auth: false,
    shell: 'bgs',
    title: 'System Picks',
    description: 'Manage system options',
  },
  {
    path: '/admin/image-queue',
    component: () => import('../pages/admin/ImageQueueMonitorPage'),
    auth: false,
    shell: 'bgs',
    title: 'Image Queue Monitor',
    description: 'Monitor image enhancement queue',
  },
  {
    path: '/admin/image-integrity',
    component: () => import('../pages/admin/ImageIntegrityPage'),
    auth: false,
    shell: 'none',
  },
  {
    path: '/admin/sync-monitoring',
    component: () => import('../pages/admin/SyncMonitoringPage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Sync Monitoring',
    description: 'Manage n8n workflow schedules and monitor data synchronization',
  },
  {
    path: '/admin/backups',
    component: () => import('../pages/admin/BackupDashboardPage.trpc'),
    auth: false,
    shell: 'bgs',
    title: 'Backup Verification',
    description: 'Monitor automated backup verification and status',
  },

  // ===========================
  // ADMIN: STOCK & INVENTORY CLEANUP
  // ===========================
  {
    path: '/admin/stock-alerts',
    component: () => import('../pages/admin/StockAlertsPage.trpc'),
    auth: false,
    shell: 'bgs',
    title: 'Stock Alerts',
    description: 'Manage low stock alert rules and monitor triggered alerts',
  },
  {
    path: '/admin/sold-items-cleanup',
    component: () => import('../pages/admin/SoldItemsCleanupPage'),
    auth: false,
    shell: 'bgs',
    title: 'Sold Items Cleanup',
    description: 'Archive and clean up zero-quantity items',
  },

  // ===========================
  // ADMIN: PRICING
  // ===========================
  {
    path: '/admin/pricing-intelligence',
    component: () => import('../pages/admin/PricingIntelligencePage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Pricing Intelligence',
    description: 'Manage pricing knowledge base and sync marketplace sales data',
  },

  // ===========================
  // ADMIN: SKU & ORPHAN RESOLUTION
  // ===========================
  {
    path: '/admin/sku-resolution',
    component: () => import('../pages/admin/SkuResolutionPage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'SKU Resolution',
    description: 'Auto-fix suggestions for unmatched eBay listings',
  },
  {
    path: '/admin/orphan-resolution',
    component: () => import('../pages/admin/OrphanResolutionPage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Orphan Resolution',
    description: 'Match unlinked eBay orders to inventory items',
  },

  // ===========================
  // ADMIN: EBAY INTEGRATION
  // ===========================
  {
    path: '/admin/ebay-settings',
    component: () => import('../pages/EbaySettingsPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Integration',
    description: 'Manage eBay connections and sync inventory',
  },
  {
    path: '/admin/ebay-categories',
    component: () => import('../pages/admin/EbayCategoryManagementPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Category Management',
    description: 'Sync, map, and manage eBay product categories',
  },
  {
    path: '/admin/ebay-synonyms',
    component: () => import('../pages/admin/EbaySynonymManagementPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Synonym Management',
    description: 'Manage intelligent category search synonyms',
  },
  {
    path: '/admin/ebay-seller-data',
    component: () => import('../pages/admin/EbaySellerDataPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Seller Data',
    description: 'Query and view eBay seller account information',
  },
  {
    path: '/admin/ebay-account',
    component: () => import('../pages/admin/EbayAccountPage'),
    auth: true,
    shell: 'bgs',
    title: 'eBay Account',
    description: 'Business policies, seller profile, marketing, and analytics',
  },
  {
    path: '/admin/ebay-export-history',
    component: () => import('../pages/EbayExportHistoryPage'),
    auth: true,
    shell: 'none',
  },
  {
    path: '/admin/vero-compliance',
    component: () => import('../pages/admin/VeroCompliancePage'),
    auth: true,
    shell: 'bgs',
    title: 'VERO Compliance',
    description: 'Pre-listing brand restriction checking',
  },
  {
    path: '/admin/ebay-shipping',
    component: () => import('../pages/admin/EbayShippingPage'),
    auth: true,
    shell: 'bgs',
    title: 'Shipping Estimates',
    description: 'Pre-listing shipping cost calculations',
  },

  // ===========================
  // ADMIN: MARKETPLACE SESSIONS
  // ===========================
  {
    path: '/admin/marketplace-sessions',
    component: () => import('../pages/admin/MarketplaceSessionsPage'),
    auth: true,
    shell: 'bgs',
    title: 'Marketplace Sessions',
    description: 'Manage cookie-based authentication for marketplaces',
  },
  {
    path: '/admin/marketplace-sales',
    component: () => import('../pages/admin/MarketplaceSalesPage'),
    auth: true,
    shell: 'bgs',
    title: 'Marketplace Sales Monitor',
    description: 'Unified sales tracking across all marketplaces',
  },
  {
    path: '/sales',
    component: () => import('../pages/SalesPage.trpc'),
    auth: true,
    shell: 'bgs',
    title: 'Sales',
    description: 'Sales tracking with tax and profit calculations',
  },

  // ===========================
  // ADMIN: WEBHOOKS & AUTOMATION
  // ===========================
  {
    path: '/admin/webhooks',
    component: () => import('../pages/admin/N8nWebhooksPage'),
    auth: true,
    shell: 'bgs',
    title: 'n8n Webhooks',
    description: 'Workflow automation triggers and monitoring',
  },

  // ===========================
  // ADMIN: SETTINGS
  // ===========================
  {
    path: '/admin/settings',
    component: () => import('../pages/SettingsPage.trpc'),
    auth: false,
    shell: 'bgs',
    title: 'Settings',
    description: 'Application settings and feature toggles',
  },

  // ===========================
  // VOICE ASSISTANT
  // ===========================
  {
    path: '/voice-assistant',
    component: () => import('../pages/VoiceAssistantPage'),
    auth: true,
    shell: 'none',
    title: 'Voice Assistant',
    description: 'Hands-free inventory management with Gemini Live API',
  },
]

/**
 * Route lookup by path
 * Useful for finding route config by path string
 */
export function getRouteConfig(path: string): RouteConfig | undefined {
  return routes.find((route) => route.path === path)
}

/**
 * Get all protected routes
 */
export function getProtectedRoutes(): RouteConfig[] {
  return routes.filter((route) => route.auth !== false)
}

/**
 * Get all public routes
 */
export function getPublicRoutes(): RouteConfig[] {
  return routes.filter((route) => route.auth === false)
}
